import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiErrorMessage } from '../api/client'
import { partnersApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { AuthLayout } from '../components/AuthLayout'
import { ErrorMessage } from '../components/ErrorMessage'

/**
 * Where Violet Dots' "Back to Yellow Dots" button lands: /partner/violetdots/return?token=...
 *
 * The token is a JWT Violet Dots made at the moment of the click, so it signs the person in however
 * long ago they left - see VioletDotsReturnService. It is posted once (a token works once), the
 * session it buys replaces any other, and the person goes on to their Certification page, where the
 * certificate they just earned is read in.
 */
export function VioletDotsReturnPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const { applyToken } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(token ? null : 'This link is missing its sign-in details.')
  // React runs effects twice in development; a second post of the same token would be refused as used.
  const sent = useRef(false)

  useEffect(() => {
    if (!token || sent.current) return
    sent.current = true
    partnersApi
      .violetDotsReturn(token)
      .then(async (res) => {
        if (!res.token) throw new Error('No session came back')
        await applyToken(res.token)
        navigate('/certification', { replace: true })
      })
      .catch((err) => setError(apiErrorMessage(err, 'We could not sign you in from Violet Dots.')))
  }, [token, applyToken, navigate])

  return (
    <AuthLayout>
      <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">Welcome back</h1>
      {error ? (
        <div className="mt-6 space-y-4">
          <ErrorMessage message={error} />
          <Link to="/login" className="btn-sun w-full">
            Sign in
          </Link>
        </div>
      ) : (
        <p className="mt-2 text-ink-500">Signing you in from Violet Dots...</p>
      )}
    </AuthLayout>
  )
}
